import React, { useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import { noop } from '../util/functions';

export type UseConfirmationOptions<C extends boolean | undefined = undefined> = {
    confirmLabel?: string;
    cancelLabel?: string;
    html?: C;
}

export interface UseConfirmationResponse<C extends boolean | undefined = undefined> {
    render(): React.ReactNode;
    confirm(message: C extends false | undefined ?  string : React.ReactNode, title: string): Promise<boolean>;
}

export const useConfirmation = <C extends boolean | undefined = undefined>(options: UseConfirmationOptions<C> = {}): UseConfirmationResponse<C> => {
    const { confirmLabel = '', cancelLabel = '' } = options || {};
    const [ open, setOpen ] = useState(false);
    const titleRef = useRef('');
    const messageRef = useRef<React.ReactNode>('');
    const [ clickHandler, setClickHandler ] = useState<(value: boolean) => () => unknown>(() => noop);

    const render: UseConfirmationResponse<C>['render'] = () => (
        <Dialog
            open={open}
            onClose={clickHandler(false)}
            fullWidth
        >
            {titleRef && titleRef.current && <DialogTitle>{titleRef.current}</DialogTitle>}
            <DialogContent>
                {options.html ? messageRef.current : (
                    <DialogContentText>{messageRef ? messageRef.current : ''}</DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={clickHandler(false)}
                >
                    {cancelLabel}
                </Button>
                <Button
                    color="primary"
                    onClick={clickHandler(true)}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );

    const confirm: UseConfirmationResponse<C>['confirm'] = async (message, title = '') => {
        messageRef.current = message;
        titleRef.current = title;
        setOpen(true);
        return new Promise((res) => {
            setClickHandler(() => (value: boolean) => () => {
                setOpen(false);
                res(value);
            });
        });
    };

    return {
        render,
        confirm,
    };
};
